import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import { listPlans, updatePlan, type AdminPlan } from '@/lib/api';

const field = 'w-full rounded-lg border border-line-strong px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

const PlanRow: React.FC<{ plan: AdminPlan; onSaved: () => void }> = ({ plan, onSaved }) => {
  const { t } = useTranslation();
  const [p, setP] = useState<AdminPlan>(plan);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirrors PlanPricing.PercentOff on the server — floored, and nothing under 1% is a badge.
  const promoPercent = (() => {
    const promo = Number(p.promoMonthlyPrice);
    const list = Number(p.monthlyPrice);
    if (!promo || !list || promo >= list) return null;
    const pct = Math.floor(((list - promo) / list) * 100);
    return pct >= 1 ? pct : null;
  })();

  const set = (patch: Partial<AdminPlan>) => { setP((prev) => ({ ...prev, ...patch })); setSaved(false); };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updatePlan(p.id, {
        name: p.name, monthlyPrice: Number(p.monthlyPrice), annualPrice: Number(p.annualPrice),
        maxCabinets: Number(p.maxCabinets), maxSeats: Number(p.maxSeats), maxDoctors: Number(p.maxDoctors),
        storageQuotaMb: Number(p.storageQuotaMb), isActive: p.isActive,
        stripePriceIdMonthly: p.stripePriceIdMonthly ?? '', stripePriceIdAnnual: p.stripePriceIdAnnual ?? '',
        promoMonthlyPrice: Number(p.promoMonthlyPrice) || null,
        promoAnnualPrice: Number(p.promoAnnualPrice) || null,
        promoUntil: p.promoUntil || null,
        promoLabel: p.promoLabel?.trim() || null,
      });
      setSaved(true);
      onSaved();
    } catch {
      setError(t('plans.saveError', "Could not save the plan."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-body">{p.name}</span>
          <span className="rounded bg-subtle px-1.5 py-0.5 text-[10px] uppercase text-muted">{p.type}</span>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-strong">
          <input type="checkbox" checked={p.isActive} onChange={(e) => set({ isActive: e.target.checked })} /> Active
        </label>
      </div>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-xs text-subtle-ink">Monthly ({p.currency})
          <input type="number" className={field} value={p.monthlyPrice} onChange={(e) => set({ monthlyPrice: Number(e.target.value) })} />
        </label>
        <label className="text-xs text-subtle-ink">Annual ({p.currency})
          <input type="number" className={field} value={p.annualPrice} onChange={(e) => set({ annualPrice: Number(e.target.value) })} />
        </label>
        <label className="text-xs text-subtle-ink">Max cabinets
          <input type="number" className={field} value={p.maxCabinets} onChange={(e) => set({ maxCabinets: Number(e.target.value) })} />
        </label>
        <label className="text-xs text-subtle-ink">Max chairs
          <input type="number" className={field} value={p.maxSeats} onChange={(e) => set({ maxSeats: Number(e.target.value) })} />
        </label>
        <label className="text-xs text-subtle-ink sm:col-span-2">Stripe price — monthly
          <input className={field} placeholder="price_…" value={p.stripePriceIdMonthly ?? ''} onChange={(e) => set({ stripePriceIdMonthly: e.target.value })} />
        </label>
        <label className="text-xs text-subtle-ink sm:col-span-2">Stripe price — annual
          <input className={field} placeholder="price_…" value={p.stripePriceIdAnnual ?? ''} onChange={(e) => set({ stripePriceIdAnnual: e.target.value })} />
        </label>
      </div>

      <div className="mt-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">{t('plans.promotion', "Promotion")}</div>
        <p className="mt-0.5 mb-2 text-xs text-muted">
          While this is set, the public site shows the normal price struck through, the new price, and
          how much is off. It stops on its own after the end date — nothing has to be turned off.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs text-subtle-ink">Promo monthly ({p.currency})
            <input
              type="number" className={field} placeholder="—"
              value={p.promoMonthlyPrice ?? ''}
              onChange={(e) => set({ promoMonthlyPrice: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </label>
          <label className="text-xs text-subtle-ink">Promo annual ({p.currency})
            <input
              type="number" className={field} placeholder="scaled"
              value={p.promoAnnualPrice ?? ''}
              onChange={(e) => set({ promoAnnualPrice: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </label>
          <label className="text-xs text-subtle-ink">{t('tenant.until', "Until")}<input
              type="date" className={field}
              value={p.promoUntil ?? ''}
              onChange={(e) => set({ promoUntil: e.target.value || null })}
            />
          </label>
          <label className="text-xs text-subtle-ink">Badge wording (optional)
            <input
              className={field} maxLength={120} placeholder={t('plans.badgeAuto', "auto: “X% off”")}
              value={p.promoLabel ?? ''}
              onChange={(e) => set({ promoLabel: e.target.value || null })}
            />
          </label>
        </div>

        {promoPercent !== null && (
          <div className="mt-2 text-xs text-muted-strong">
            The site will show{' '}
            <span className="text-subtle-ink line-through">{p.currency} {p.monthlyPrice}</span>{' '}
            <span className="font-semibold text-body">{p.currency} {p.promoMonthlyPrice}</span>{' '}
            <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-medium text-amber-900">
              {p.promoLabel?.trim() || `${promoPercent}% off`}
              {p.promoUntil ? ` until ${p.promoUntil}` : ''}
            </span>
          </div>
        )}
        {p.promoMonthlyPrice !== null && promoPercent === null && (
          <div className="mt-2 text-xs text-red-600">
            A promotional price has to be below {p.currency} {p.monthlyPrice}, and at least 1% below to
            be worth advertising.
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button variant="secondary" onClick={save} loading={saving}>{t('common.save', "Save")}</Button>
        {saved && <span className="text-sm text-brand-ink">{t('common.saved', "Saved.")}</span>}
      </div>
    </Card>
  );
};

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<AdminPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => listPlans().then(setPlans).catch(() => setError(t('plans.loadError', "Could not load plans.")));
  useEffect(() => { load(); }, []);

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!plans) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-body">{t('plans.title', "Plans")}</h1>
      <p className="text-sm text-muted">{t('plans.stripeHint', "Set each plan’s Stripe price ids to enable online subscriptions.")}</p>
      <div className="space-y-4">
        {plans.map((p) => <PlanRow key={p.id} plan={p} onSaved={load} />)}
      </div>
    </div>
  );
};

export default PlansPage;
